import { Form, Input, Typography, Button } from 'antd';
import { Select } from '../DataEntry';
import { Trans, t } from '@lingui/macro';
import React, { useRef } from 'react';
import countryCodes from 'bukku/containers/AppPage/ControlPanel/Profile/countryCodes.json';
import { isPossiblePhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { ConditionalTooltip } from '../Tooltip/ConditionalTooltip';
import { StyledPhoneNumberInputV3Wrapper } from './style';

const { OptGroup, Option } = Select;
const { Text } = Typography;

export const phoneNumCharRegex = /^[\d\s-]+$/; // only allows digits, spaces and dashes
export const getPhoneNumCharError = phoneNo => !(phoneNumCharRegex.test(phoneNo));

// If want to use form to check if first phone number has any form validation errors, use this constant.
// The format: [Form.List name, form item index, form item name]
export const myInvoisPhoneNumFormItemName = ['phone_no', 0, 'phone_no'];

export const parsePhoneNumbersToString = (allPhoneNums = [], returnRawData = false) => {
    const newArr = allPhoneNums?.map(phoneObj => {
        const {
            phone_no: phoneNum,
            phone_no_code: phoneCode,
        } = phoneObj;

        if (!phoneNum && !phoneCode)
            return null;

        let parsedPhoneNumber = phoneNum;

        // If Malaysia phone code, remove any leading 0s
        if (phoneCode === '+60' && parsedPhoneNumber)
            parsedPhoneNumber = parsedPhoneNumber.replace(/^0+/, '');

        try {
            return parsePhoneNumber(`${phoneCode}${parsedPhoneNumber}`)?.number?.slice(1); // slice(1) to remove the leading '+'
        }
        catch (err) {
            if (returnRawData)
                return `${phoneCode || ''}${parsedPhoneNumber || ''}`;
            console.log("parsePhoneNumber error in ContactView", err.message);
        }

        return null;
    }).filter(p => p !== null);

    return newArr.join(",");
};

export default function PhoneNumberInputV3(props) {
    const {
        label,
        required,
        popupContainerId,
        // formRef,
        form,
        initialValue, // the entire phone number, without "+", eg "60123344556", "0123344556", "6588881111,601233443322"
        readOnly,
        disabled,
        onPhoneNoChange = () => {},
        onPhoneNoCodeChange = () => {},
        maxNumbersAllowed = 0, // can add up to how many phone numbers
    } = props;

    const hasActions = !readOnly && maxNumbersAllowed > 0;

    const inputRefs = useRef({});

    const renderCountryCodeSelect = (fieldName, idx) => {
        const popularCountries = [];
        const otherCountries = [];

        const renderOption = item => (
            <Option
                key={item.code}
                value={item.dial_code}
                label={`${item.emoji} ${item.name} ${item.dial_code}`}
            >
                {item.emoji}
                &nbsp;
                {item.name}
                &nbsp;
                <Text type="secondary">{`${item.dial_code}`}</Text>
            </Option>
        );

        countryCodes.forEach(item => {
            if (['SG', 'MY', 'ID'].includes(item.code))
                popularCountries.push(renderOption(item));
            else
                otherCountries.push(renderOption(item));
        });

        return (
            <Form.Item
                name={[fieldName, "phone_no_code"]}
                noStyle
            >
                <Select
                    ref={el => {
                        inputRefs.current[idx] = el;
                    }}
                    style={{minWidth: "74.5px"}}
                    showSearch
                    placeholder={<Trans>Code</Trans>}
                    allowClear
                    popupContainerId={popupContainerId}
                    dropdownMatchSelectWidth={false}
                    filterOption
                    optionFilterProp="label"
                    optionLabelProp="value"
                    readOnly={readOnly}
                    disabled={disabled}
                    onChange={value => {
                        // putting this in 'dependencies' of phone_no input does not work until u edited the phone_no input
                        // So manually call validation on the phone_no input here
                        form?.validateFields([["phone_no", fieldName, "phone_no"]]).then(() => {
                            //
                        }).catch(() => {
                            //
                        }).finally(() => {
                            if (idx === 0) // The myinvois requirement field points to the first phone num only.
                                onPhoneNoCodeChange(value);
                        });
                    }}
                >
                    <>
                        <OptGroup key="popular-countries" label={<Trans>Popular Countries</Trans>}>
                            {popularCountries}
                        </OptGroup>
                        <OptGroup key="other-countries" label={<Trans>Other Countries</Trans>} p>
                            {otherCountries}
                        </OptGroup>
                    </>
                </Select>
            </Form.Item>
        );
    };

    const parsePhoneNumbersStringToArray = value => {
        if (value) {
            const phoneNumArray = value.split(",").map(phoneNum => {
                let countryCallingCode, nationalNumber;
                try {
                    ({countryCallingCode, nationalNumber} = parsePhoneNumber(`+${phoneNum}`));
                }
                catch (err) {
                    //
                }

                const countryCodeToSet = countryCallingCode ? `+${countryCallingCode}` : null;
                const phoneNumToSet = nationalNumber || phoneNum;

                return {
                    phone_no: phoneNumToSet,
                    phone_no_code: countryCodeToSet,
                };
            });
            return phoneNumArray;
        }

        // no initial value, populate with one empty row
        return [{
            phone_no: null,
            phone_no_code: null,
        }];
    };

    const getFirstPhoneNumShouldBeFilled = currentPhoneNums => {
        if (currentPhoneNums?.length > 0) {
            // check if first is empty
            const firstPhoneNumEmpty = !currentPhoneNums[0].phone_no && !currentPhoneNums[0].phone_no_code;

            // check if 2nd onwards has value
            const secondOnwardsHasValue = currentPhoneNums
                .slice(1)
                .some(item => item?.phone_no || item?.phone_no_code);

            if (firstPhoneNumEmpty && secondOnwardsHasValue)
                return true;
        }

        return false;
    };

    return (
        <StyledPhoneNumberInputV3Wrapper hasActions={hasActions}>
            <Form.Item label={label}>
                <Form.List
                    name="phone_no"
                    initialValue={parsePhoneNumbersStringToArray(initialValue)}
                >
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map((field, idx) => {
                                const {
                                    key, name: fieldName, ...restField
                                } = field;

                                return (
                                    <div key={key} style={{ display: 'flex', marginBottom: 8, width: "100%" }}>
                                        <Form.Item
                                            style={{ flex: 1 }}
                                            {...restField}
                                            name={[fieldName, 'phone_no']}
                                            validateTrigger={['onChange', 'onBlur']}
                                            validateFirst // if a rule validation fails, stop and not validate the other rules
                                            rules={[
                                                {
                                                    required: !!required,
                                                    message: '',
                                                },
                                                ({ getFieldValue, getFieldsValue }) => ({
                                                    validator(_, value) {
                                                        const phoneNoCode = getFieldValue(["phone_no", fieldName, "phone_no_code"]);

                                                        // If all phone numbers are empty, no need to validate and remove all errors
                                                        const allPhoneNums = getFieldsValue().phone_no;
                                                        if (allPhoneNums?.every(
                                                            item => !item.phone_no && !item.phone_no_code)) {
                                                            if (idx > 0) {
                                                                form?.setFields([{
                                                                    name: ["phone_no", 0, "phone_no"],
                                                                    errors: [],
                                                                }]);
                                                                return Promise.resolve();
                                                            }
                                                            return Promise.resolve();
                                                        }

                                                        // If the first phone number is empty but the rest have values, show error on the first phone number
                                                        if (maxNumbersAllowed &&
                                                            getFirstPhoneNumShouldBeFilled(allPhoneNums)) {
                                                            const errorMsg = t`Please set a primary contact number before adding supplementary numbers.`;
                                                            if (idx > 0) {
                                                                form?.setFields([{
                                                                    name: ["phone_no", 0, "phone_no"],
                                                                    errors: [errorMsg],
                                                                }]);
                                                                return Promise.resolve();
                                                            }
                                                            return Promise.reject(new Error(errorMsg));
                                                        }

                                                        // If one exists but not the other, OR if required but one of them is missing, show error
                                                        if (((phoneNoCode && !value) ||
                                                            (value && !phoneNoCode)) ||
                                                            (required && (!value || !phoneNoCode))
                                                        )
                                                            return Promise.reject(new Error(t`Contact No. is required with country code.`));

                                                        // If has non-supported characters, show error
                                                        if (value && getPhoneNumCharError(value))
                                                            return Promise.reject(new Error(t`Only numbers, dashes and spaces are allowed in Contact No.`));

                                                        return Promise.resolve();
                                                    },
                                                }),
                                                ({ getFieldValue }) => ({
                                                    validator(_, value) {
                                                        if (!value)
                                                            return Promise.resolve();

                                                        // Check if its possible phone number (length only) onblur
                                                        // Ref: https://www.npmjs.com/package/libphonenumber-js#using-phone-number-validation-feature:~:text=isPossiblePhoneNumber()%20only%20validates%20phone%20number%20length%2C
                                                        let parsedPhoneNumber = value;
                                                        const phoneNoCode = getFieldValue(["phone_no", fieldName, "phone_no_code"]);

                                                        if (idx === 0)
                                                            onPhoneNoChange(value); // force set again to re-render & update the row section title & Form.Item label's myInvois icon

                                                        if (phoneNoCode === '+60') // If Malaysia phone code, remove any leading 0s
                                                            parsedPhoneNumber = parsedPhoneNumber.replace(/^0+/, '');

                                                        if (phoneNoCode && parsedPhoneNumber && !isPossiblePhoneNumber(`${phoneNoCode}${parsedPhoneNumber}`))
                                                            return Promise.reject(new Error(t`Please enter a valid phone number.`));
                                                        return Promise.resolve();
                                                    },
                                                    validateTrigger: 'onBlur',
                                                }),
                                            ]}
                                        >
                                            <Input
                                                placeholder="321988888"
                                                addonBefore={renderCountryCodeSelect(fieldName, idx)}
                                                style={{padding: 0, width: "100%"}}
                                                readOnly={readOnly}
                                                disabled={disabled}
                                                onChange={e => {
                                                    if (idx === 0) // The myinvois requirement field points to the first phone num only.
                                                        onPhoneNoChange(e.target.value);
                                                }}
                                            />
                                        </Form.Item>
                                        {hasActions && (
                                            <span className="action-button-wrapper">
                                                {idx > 0 ?
                                                    <Button
                                                        onClick={() => {
                                                            remove(fieldName);

                                                            // validate first phone number again
                                                            form?.validateFields([["phone_no", 0, "phone_no"]]).then(() => {
                                                            //
                                                            }).catch(() => {
                                                            //
                                                            });
                                                        }}
                                                    >
                                                        <MinusOutlined style={{marginRight: 0}} />
                                                    </Button>
                                                    :
                                                    <ConditionalTooltip
                                                        rules={[{
                                                            condition: fields.length === maxNumbersAllowed,
                                                            message: t`Only a maximum of ${maxNumbersAllowed} phone numbers are allowed.`,
                                                        }]}
                                                    >
                                                        <Button
                                                            disabled={fields.length === maxNumbersAllowed}
                                                            onClick={() => {
                                                                add();
                                                                const index = fields.length;
                                                                // Wait for country code select to render and focus on it
                                                                setTimeout(() => {
                                                                    inputRefs.current[index]?.focus();
                                                                }, 0);
                                                            }}
                                                        >
                                                            <PlusOutlined style={{marginRight: 0}} />
                                                        </Button>
                                                    </ConditionalTooltip>}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </>
                    )}
                </Form.List>
            </Form.Item>
        </StyledPhoneNumberInputV3Wrapper>
    );
}
